import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  FileText, Package, AlertTriangle, CheckCircle2, Download,
  ArrowLeft, ExternalLink, ChevronDown, ChevronUp, Search, Filter,
  Activity, Star, GitFork, Users, Clock, Shield, TrendingUp,
  AlertCircle, Info, X, RefreshCw, Scale, Lock, Unlock, GitPullRequest
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  getSBOMReport,
  exportSBOMCycloneDX,
  exportSBOMSPDX,
  exportSBOMCSV,
  exportSBOMJSON,
  exportSBOMPDF,
  rescanSBOM,
  getSBOMSecurityOverview,
  getSBOMLicenseAnalysis,
  getHealthScoreColor,
  getHealthScoreBg,
  getMaintenanceStatusColor,
  getVulnerabilityBadge,
  formatNumber,
  getEcosystemIcon,
  getEcosystemColor
} from '@/services/sbom';

const SBOMDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const { sbomId } = useParams();
  const [sbom, setSbom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterVulnerable, setFilterVulnerable] = useState(false);
  const [filterOutdated, setFilterOutdated] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const [securityOverview, setSecurityOverview] = useState(null);
  const [licenseAnalysis, setLicenseAnalysis] = useState(null);
  const [exporting, setExporting] = useState(false);
  // CISA/G7 May 2026 SBOM compliance state
  const [cisaCompliance, setCisaCompliance] = useState(null);
  const [cisaBusy, setCisaBusy] = useState(false);
  // Signed Verify Report PDF download
  const [verifyPdfBusy, setVerifyPdfBusy] = useState(false);

  useEffect(() => {
    loadSBOM();
    loadSecurityOverview();
    loadLicenseAnalysis();
    loadCisaCompliance();
  }, [sbomId]);

  const loadCisaCompliance = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/litesabre/sbom/reports/${sbomId}/cisa-2026-compliance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) setCisaCompliance(await r.json());
    } catch (err) {
      console.error('Failed to load CISA compliance:', err);
    }
  };

  const handleDownloadVerifyReport = async () => {
    // Downloads the Beskar-signed PDF of the SBOM signature verification
    // result. Backend endpoint returns application/pdf with a
    // Content-Disposition header — we save as a Blob to preserve binary
    // integrity so the auditor can re-verify the embedded signature.
    if (!sbom?.sbom_author_signature) {
      toast.error('This SBOM is not signed yet — run "Enrich for CISA 2026" first');
      return;
    }
    setVerifyPdfBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/litesabre/sbom/reports/${sbomId}/verify-report.pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) {
        const detail = await r.text().catch(() => '');
        throw new Error(detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sbom-verify-${sbomId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const hash = r.headers.get('X-Beskar-Verification-Hash');
      const signed = r.headers.get('X-Beskar-Signature-Present') === 'true';
      toast.success(
        signed
          ? `Verify Report downloaded — Beskar-signed (hash ${hash ? hash.slice(0, 12) + '…' : 'n/a'})`
          : 'Verify Report downloaded — WARNING: SP key unavailable, PDF unsigned'
      );
    } catch (err) {
      console.error('Verify report download failed:', err);
      toast.error(`Failed to download verify report: ${err.message}`);
    } finally {
      setVerifyPdfBusy(false);
    }
  };

  const handleCisaBackfill = async () => {
    setCisaBusy(true);
    try {
      const token = localStorage.getItem('token');
      // Full enrichment endpoint: SBOM-level defaults + PURL derivation +
      // real integrity hashes from registries + RSA-PSS digital signature.
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/litesabre/sbom/reports/${sbomId}/cisa-2026-enrich`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}' }
      );
      const data = await r.json();
      if (data.compliance) setCisaCompliance(data.compliance);
      const c = data.counters || {};
      toast.success(
        `CISA 2026: ${(data.compliance.cisa_2026_score * 100).toFixed(1)}% ` +
        `(${data.compliance.cisa_2026_level}) — ${c.purl_added || 0} PURL(s), ` +
        `${c.hash_real || 0} registry hash(es), ${c.hash_fallback || 0} content-address ` +
        `${data.signature_present ? '· signed' : ''}`
      );
      await loadSBOM();
    } catch (err) {
      toast.error('Enrichment failed');
    } finally {
      setCisaBusy(false);
    }
  };

  const loadSBOM = async () => {
    try {
      setLoading(true);
      const data = await getSBOMReport(sbomId);
      setSbom(data);
    } catch (err) {
      console.error('Failed to load SBOM:', err);
      toast.error('Failed to load SBOM report');
    } finally {
      setLoading(false);
    }
  };

  const loadSecurityOverview = async () => {
    try {
      const data = await getSBOMSecurityOverview(sbomId);
      setSecurityOverview(data);
    } catch (err) {
      console.error('Failed to load security overview:', err);
    }
  };

  const loadLicenseAnalysis = async () => {
    try {
      const data = await getSBOMLicenseAnalysis(sbomId);
      setLicenseAnalysis(data);
    } catch (err) {
      console.error('Failed to load license analysis:', err);
    }
  };

  const handleRescan = async () => {
    try {
      setRescanning(true);
      const result = await rescanSBOM(sbomId);
      toast.success('SBOM rescan initiated');
      navigate(`${basePath}/sbom/${result.new_sbom_id}`);
    } catch (err) {
      console.error('Rescan failed:', err);
      toast.error('Failed to initiate rescan');
    } finally {
      setRescanning(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      await exportSBOMPDF(sbomId);
      toast.success('PDF report downloaded');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async (format) => {
    try {
      switch (format) {
        case 'cyclonedx':
          await exportSBOMCycloneDX(sbomId);
          break;
        case 'spdx':
          await exportSBOMSPDX(sbomId);
          break;
        case 'csv':
          await exportSBOMCSV(sbomId);
          break;
        case 'json':
          await exportSBOMJSON(sbomId);
          break;
        default:
          break;
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const sortedAndFilteredDeps = useMemo(() => {
    if (!sbom?.dependencies) return [];
    
    let filtered = sbom.dependencies.filter(dep => {
      const matchesSearch = dep.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesVulnerable = !filterVulnerable || dep.has_issues;
      const matchesOutdated = !filterOutdated || dep.version !== dep.latest_version;
      return matchesSearch && matchesVulnerable && matchesOutdated;
    });
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'health':
          aVal = a.health?.score || 0;
          bVal = b.health?.score || 0;
          break;
        case 'vulnerabilities':
          aVal = (a.vulnerabilities?.critical || 0) * 1000 + 
                 (a.vulnerabilities?.high || 0) * 100 + 
                 (a.vulnerabilities?.medium || 0) * 10 + 
                 (a.vulnerabilities?.low || 0);
          bVal = (b.vulnerabilities?.critical || 0) * 1000 + 
                 (b.vulnerabilities?.high || 0) * 100 + 
                 (b.vulnerabilities?.medium || 0) * 10 + 
                 (b.vulnerabilities?.low || 0);
          break;
        case 'downloads':
          aVal = a.health?.weekly_downloads || 0;
          bVal = b.health?.weekly_downloads || 0;
          break;
        default:
          aVal = a.name;
          bVal = b.name;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [sbom, searchTerm, filterVulnerable, filterOutdated, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 opacity-30" />;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!sbom) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">SBOM report not found</p>
        <Button onClick={() => navigate(`${basePath}/sbom`)} className="mt-4">
          Back to Reports
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}/sbom`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-7 h-7 text-indigo-600" />
              {sbom.name}
            </h1>
            <p className="text-gray-500 mt-1 text-sm truncate max-w-xl">{sbom.target}</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700 gap-2">
              <Download className="w-4 h-4" />
              Export SBOM
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleExport('cyclonedx')}>
              CycloneDX 1.5 (JSON)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('spdx')}>
              SPDX 2.3 (JSON)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('csv')}>
              CSV Spreadsheet
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('json')}>
              Raw JSON
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* CISA + G7 May 2026 SBOM Minimum Elements — compliance banner */}
      {cisaCompliance && (
        <Card
          className={`border-l-4 ${
            cisaCompliance.cisa_2026_level === 'conformant' ? 'border-l-emerald-500 bg-emerald-50/40'
            : cisaCompliance.cisa_2026_level === 'partial' ? 'border-l-amber-500 bg-amber-50/40'
            : 'border-l-rose-500 bg-rose-50/40'
          }`}
          data-testid="sbom-cisa2026-banner"
        >
          <CardContent className="py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-semibold">CISA + G7 SBOM Minimum Elements — May 2026</p>
                  <Badge
                    className={
                      cisaCompliance.cisa_2026_level === 'conformant' ? 'bg-emerald-500 text-white'
                      : cisaCompliance.cisa_2026_level === 'partial' ? 'bg-amber-500 text-white'
                      : 'bg-rose-500 text-white'
                    }
                    data-testid="sbom-cisa2026-level"
                  >
                    {(cisaCompliance.cisa_2026_score * 100).toFixed(1)}% · {cisaCompliance.cisa_2026_level.toUpperCase()}
                  </Badge>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {cisaCompliance.component_coverage?.with_hash || 0}/{cisaCompliance.component_coverage?.total || 0} components have hashes ·
                  {' '}{cisaCompliance.component_coverage?.with_license || 0} with licenses ·
                  {' '}{cisaCompliance.component_coverage?.with_producer || 0} with producer ·
                  {' '}{cisaCompliance.component_coverage?.with_identifier || 0} with PURL/CPE/SWHID.
                </p>
                <details className="mt-2">
                  <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-500" data-testid="sbom-cisa2026-details-toggle">
                    Show 17 minimum-element checklist
                  </summary>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-[11px]">
                    {[
                      ...(cisaCompliance.sbom_level_elements || []),
                      ...(cisaCompliance.component_level_elements || []),
                      ...(cisaCompliance.practices_elements || []),
                    ].map((el) => (
                      <div key={el.name} className="flex items-start gap-2 p-2 bg-white rounded border" data-testid={`sbom-cisa2026-row-${el.name.replace(/\s+/g, '-').toLowerCase()}`}>
                        <span className={
                          el.status === 'present' ? 'text-emerald-600'
                          : el.status === 'partial' ? 'text-amber-500'
                          : el.status === 'unknown' ? 'text-slate-500'
                          : 'text-rose-500'
                        }>●</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{el.name}</p>
                          <p className="text-gray-500 truncate">{el.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {cisaCompliance.cisa_2026_level !== 'conformant' && (
                  <Button
                    size="sm"
                    onClick={handleCisaBackfill}
                    disabled={cisaBusy}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="sbom-cisa2026-backfill-btn"
                  >
                    {cisaBusy ? 'Enriching…' : 'Enrich for CISA 2026'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadVerifyReport}
                  disabled={verifyPdfBusy || !sbom?.sbom_author_signature}
                  title={
                    sbom?.sbom_author_signature
                      ? 'Download a Beskar-signed PDF containing the full SBOM (components, hashes, PURLs, licenses), the CISA + G7 May 2026 checklist, and the signature verification attestation'
                      : 'SBOM must be signed first — run Enrich for CISA 2026'
                  }
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="sbom-verify-report-pdf-btn"
                >
                  {verifyPdfBusy ? 'Preparing PDF…' : 'Download Verify Report (PDF)'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{sbom.summary?.total_dependencies || 0}</p>
              <p className="text-xs text-gray-500">Dependencies</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{sbom.summary?.critical_vulnerabilities || 0}</p>
              <p className="text-xs text-gray-500">Critical</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{sbom.summary?.high_vulnerabilities || 0}</p>
              <p className="text-xs text-gray-500">High</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-500">{sbom.summary?.medium_vulnerabilities || 0}</p>
              <p className="text-xs text-gray-500">Medium</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-500">{sbom.summary?.low_vulnerabilities || 0}</p>
              <p className="text-xs text-gray-500">Low</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-center">
              <p className={`text-2xl font-bold ${getHealthScoreColor(sbom.summary?.average_health_score || 0)}`}>
                {Math.round(sbom.summary?.average_health_score || 0)}
              </p>
              <p className="text-xs text-gray-500">Health Score</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* License Breakdown */}
      {sbom.summary?.licenses && Object.keys(sbom.summary.licenses).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">License Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(sbom.summary.licenses).map(([license, count]) => (
                <span 
                  key={license} 
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  {license}: {count}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search dependencies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="sbom-dep-search"
                />
              </div>
            </div>
            <Button
              variant={filterVulnerable ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterVulnerable(!filterVulnerable)}
              className={filterVulnerable ? "bg-red-500 hover:bg-red-600" : ""}
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Vulnerable ({sbom.summary?.vulnerable_dependencies || 0})
            </Button>
            <Button
              variant={filterOutdated ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterOutdated(!filterOutdated)}
              className={filterOutdated ? "bg-orange-500 hover:bg-orange-600" : ""}
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              Outdated ({sbom.summary?.outdated_count || 0})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dependencies Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Dependencies</CardTitle>
          <CardDescription>
            Showing {sortedAndFilteredDeps.length} of {sbom.dependencies?.length || 0} dependencies
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sbom-dependencies-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th 
                    className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Dependency <SortIcon field="name" />
                    </div>
                  </th>
                  <th className="text-left p-3 font-medium">Version</th>
                  <th className="text-left p-3 font-medium">Latest</th>
                  <th className="text-left p-3 font-medium">Published</th>
                  <th 
                    className="text-center p-3 font-medium cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('vulnerabilities')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Vulnerabilities <SortIcon field="vulnerabilities" />
                    </div>
                  </th>
                  <th className="text-center p-3 font-medium">Fixable</th>
                  <th className="text-left p-3 font-medium">License</th>
                  <th 
                    className="text-center p-3 font-medium cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('health')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Health <SortIcon field="health" />
                    </div>
                  </th>
                  <th className="text-center p-3 font-medium">Issues</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredDeps.map((dep, idx) => (
                  <tr 
                    key={dep.id || idx} 
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedPackage(dep)}
                    data-testid={`dep-row-${dep.name}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getEcosystemIcon(dep.ecosystem)}</span>
                        <div>
                          <p className="font-medium text-gray-900">{dep.name}</p>
                          <p className="text-xs text-gray-400">
                            {dep.is_direct ? 'Direct' : 'Transitive'}
                            {dep.is_dev_dependency && ' • Dev'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">{dep.version}</code>
                    </td>
                    <td className="p-3">
                      {dep.latest_version && dep.latest_version !== dep.version ? (
                        <span className="text-orange-600 font-medium">{dep.latest_version}</span>
                      ) : (
                        <span className="text-green-600">{dep.latest_version || dep.version}</span>
                      )}
                    </td>
                    <td className="p-3 text-gray-500 text-xs">
                      {dep.last_published || '-'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        {dep.vulnerabilities?.critical > 0 && (
                          <span className="w-6 h-6 flex items-center justify-center bg-red-600 text-white rounded text-xs font-bold">
                            {dep.vulnerabilities.critical}
                          </span>
                        )}
                        {dep.vulnerabilities?.high > 0 && (
                          <span className="w-6 h-6 flex items-center justify-center bg-orange-500 text-white rounded text-xs font-bold">
                            {dep.vulnerabilities.high}
                          </span>
                        )}
                        {dep.vulnerabilities?.medium > 0 && (
                          <span className="w-6 h-6 flex items-center justify-center bg-yellow-500 text-black rounded text-xs font-bold">
                            {dep.vulnerabilities.medium}
                          </span>
                        )}
                        {dep.vulnerabilities?.low > 0 && (
                          <span className="w-6 h-6 flex items-center justify-center bg-blue-500 text-white rounded text-xs font-bold">
                            {dep.vulnerabilities.low}
                          </span>
                        )}
                        {(!dep.vulnerabilities?.critical && !dep.vulnerabilities?.high && 
                          !dep.vulnerabilities?.medium && !dep.vulnerabilities?.low) && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      {(dep.vulnerabilities?.critical > 0 || dep.vulnerabilities?.high > 0 || 
                        dep.vulnerabilities?.medium > 0 || dep.vulnerabilities?.low > 0) ? (
                        <div className="flex flex-col items-center">
                          {dep.vulnerability_details?.some(v => v.is_fixable) ? (
                            <>
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Yes</Badge>
                              <span className="text-xs text-gray-500 mt-1">
                                {dep.vulnerability_details?.find(v => v.fixed_version)?.fixed_version || 'Latest'}
                              </span>
                            </>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">No</Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                        {dep.license || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center">
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getHealthScoreBg(dep.health?.score || 0)} ${getHealthScoreColor(dep.health?.score || 0)}`}
                        >
                          {dep.health?.score || 0}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      {dep.dependencies_with_issues > 0 ? (
                        <span className="text-red-500 font-medium">{dep.dependencies_with_issues}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {sortedAndFilteredDeps.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No dependencies match your filters
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Package Detail Modal */}
      <Dialog open={!!selectedPackage} onOpenChange={() => setSelectedPackage(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedPackage && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="text-2xl">{getEcosystemIcon(selectedPackage.ecosystem)}</span>
                  <div>
                    <span className="text-xl">{selectedPackage.name}</span>
                    <span className="text-gray-400 ml-2">v{selectedPackage.version}</span>
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Health Score Section */}
                <div className="flex items-start gap-6">
                  <div className="text-center">
                    <div 
                      className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold ${getHealthScoreBg(selectedPackage.health?.score || 0)} ${getHealthScoreColor(selectedPackage.health?.score || 0)}`}
                    >
                      {selectedPackage.health?.score || 0}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Package Health</p>
                  </div>
                  
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Popularity</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="font-medium">{formatNumber(selectedPackage.health?.github_stars || 0)}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Weekly Downloads</p>
                      <p className="font-medium">{formatNumber(selectedPackage.health?.weekly_downloads || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Forks</p>
                      <div className="flex items-center gap-2 mt-1">
                        <GitFork className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{formatNumber(selectedPackage.health?.forks || 0)}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Contributors</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{selectedPackage.health?.contributors || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Maintenance Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-2">Maintenance</p>
                    <span className={`px-3 py-1 rounded-full text-sm ${getMaintenanceStatusColor(selectedPackage.health?.maintenance_status || 'unknown')}`}>
                      {selectedPackage.health?.maintenance_status || 'Unknown'}
                    </span>
                    <div className="mt-3 text-sm text-gray-600">
                      <p>Last Release: {selectedPackage.health?.last_release || 'N/A'}</p>
                      <p>Last Commit: {selectedPackage.health?.last_commit || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-2">Security</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {selectedPackage.health?.has_security_policy ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-yellow-500" />
                        )}
                        <span className="text-sm">Security Policy</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {(selectedPackage.vulnerability_details?.length > 0 || 
                          selectedPackage.vulnerabilities?.critical > 0 || 
                          selectedPackage.vulnerabilities?.high > 0 ||
                          selectedPackage.vulnerabilities?.medium > 0 ||
                          selectedPackage.vulnerabilities?.low > 0) ? (
                          <>
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                            <span className="text-sm text-red-600 font-medium">Known Vulnerabilities Found</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-sm">No Known Vulnerabilities</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vulnerabilities */}
                {(selectedPackage.vulnerabilities?.critical > 0 || 
                  selectedPackage.vulnerabilities?.high > 0 ||
                  selectedPackage.vulnerabilities?.medium > 0 ||
                  selectedPackage.vulnerabilities?.low > 0) && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-xs text-red-600 uppercase mb-3 font-medium">Vulnerabilities</p>
                    <div className="flex gap-4">
                      {selectedPackage.vulnerabilities?.critical > 0 && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-red-600">{selectedPackage.vulnerabilities.critical}</p>
                          <p className="text-xs text-red-500">Critical</p>
                        </div>
                      )}
                      {selectedPackage.vulnerabilities?.high > 0 && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-orange-600">{selectedPackage.vulnerabilities.high}</p>
                          <p className="text-xs text-orange-500">High</p>
                        </div>
                      )}
                      {selectedPackage.vulnerabilities?.medium > 0 && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-yellow-600">{selectedPackage.vulnerabilities.medium}</p>
                          <p className="text-xs text-yellow-500">Medium</p>
                        </div>
                      )}
                      {selectedPackage.vulnerabilities?.low > 0 && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{selectedPackage.vulnerabilities.low}</p>
                          <p className="text-xs text-blue-500">Low</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Detailed Vulnerability List */}
                {selectedPackage.vulnerability_details?.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <p className="text-xs text-gray-500 uppercase mb-3 font-medium">Vulnerability Details</p>
                    <div className="space-y-3">
                      {selectedPackage.vulnerability_details.map((vuln, idx) => (
                        <div key={idx} className="p-3 bg-white rounded border-l-4 border-l-red-500">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <a 
                                  href={vuln.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="font-mono text-sm text-indigo-600 hover:underline"
                                >
                                  {vuln.cve_id}
                                </a>
                                <Badge className={`text-xs ${
                                  vuln.severity === 'critical' ? 'bg-red-600 text-white' :
                                  vuln.severity === 'high' ? 'bg-orange-500 text-white' :
                                  vuln.severity === 'medium' ? 'bg-yellow-500 text-black' :
                                  'bg-blue-500 text-white'
                                }`}>
                                  {vuln.severity?.toUpperCase()}
                                </Badge>
                              </div>
                              <p className="text-sm font-medium text-gray-900">{vuln.title}</p>
                              <p className="text-xs text-gray-500 mt-1">{vuln.description}</p>
                              {vuln.published_date && (
                                <p className="text-xs text-gray-400 mt-1">Published: {vuln.published_date}</p>
                              )}
                            </div>
                            <div className="ml-4 text-right">
                              {vuln.is_fixable ? (
                                <div className="text-center">
                                  <Badge className="bg-green-100 text-green-700">Fixable</Badge>
                                  <p className="text-xs text-gray-600 mt-1">
                                    Upgrade to <span className="font-mono font-medium">{vuln.fixed_version}</span>
                                  </p>
                                </div>
                              ) : (
                                <Badge className="bg-red-100 text-red-700">No Fix</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Community & Links */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-2">Community</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        {selectedPackage.health?.has_readme ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 text-gray-400" />
                        )}
                        <span>README</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedPackage.health?.has_contributing ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 text-gray-400" />
                        )}
                        <span>Contributing Guide</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedPackage.health?.has_code_of_conduct ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 text-gray-400" />
                        )}
                        <span>Code of Conduct</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-2">Issues & PRs</p>
                    <div className="space-y-2 text-sm">
                      <p>Open Issues: <span className="font-medium">{selectedPackage.health?.open_issues || 0}</span></p>
                      <p>Open PRs: <span className="font-medium">{selectedPackage.health?.open_prs || 0}</span></p>
                      <p>Dependents: <span className="font-medium">{formatNumber(selectedPackage.health?.dependents || 0)}</span></p>
                    </div>
                  </div>
                </div>

                {/* Package Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-2">Package Information</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-gray-500">License:</span> {selectedPackage.license}</p>
                    <p><span className="text-gray-500">Ecosystem:</span> {selectedPackage.ecosystem}</p>
                    <p><span className="text-gray-500">Type:</span> {selectedPackage.is_direct ? 'Direct' : 'Transitive'}</p>
                    <p><span className="text-gray-500">Dev Dep:</span> {selectedPackage.is_dev_dependency ? 'Yes' : 'No'}</p>
                  </div>
                  {selectedPackage.repository && (
                    <a 
                      href={selectedPackage.repository} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-indigo-600 hover:underline text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Repository
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SBOMDetailPage;
